import { ActivityText } from "@components/ActivityText/ActivityText";
import { Box, Button, Typography } from "@mui/material";
import type { Activity } from "@app-types/activity-data.types";
import { theme } from "@theme";

type TaskOptionButtonProps = {
  activity: Activity;
  selected: boolean;
  onClick: () => void;
  levelDetailsLabel: string;
};

const taskButtonStyling = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  padding: 1.625,
  border: `1px solid ${theme.palette.info.main}`,
  borderRadius: 2,
  color: "var(--text)",
  background: theme.palette.secondary.main,
  textAlign: "left",
  textTransform: "none",

  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
};

const taskButtonSelectedStyling = {
  boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  borderColor: theme.palette.primary.main,
};

const baseDifficultyStyling = {
  padding: "2px 8px",
  borderRadius: 1,
  color: theme.palette.text.primary,
  fontSize: "0.75rem",
};

const beginnerDifficultyStyling = {
  background: theme.palette.warning.light,
};

const intermediateDifficultyStyling = {
  background: theme.palette.warning.main,
};
const advancedDifficultyStyling = {
  color: theme.palette.primary.contrastText,
  background: theme.palette.primary.dark,
};

export const TaskOptionButton = ({
  activity,
  selected,
  onClick,
  levelDetailsLabel,
}: TaskOptionButtonProps) => {
  const description = activity.content.paragraphs[0]?.replace(
    "Objective: ",
    "",
  );
  const buttonStyling = () => {
    let styling = { ...taskButtonStyling };
    if (selected) {
      styling = { ...styling, ...taskButtonSelectedStyling };
    }

    return styling;
  };

  const difficultyStyling = () => {
    let styling = { ...baseDifficultyStyling };

    if (levelDetailsLabel === "Beginner") {
      styling = { ...styling, ...beginnerDifficultyStyling };
    } else if (levelDetailsLabel === "Intermediate") {
      styling = { ...styling, ...intermediateDifficultyStyling };
    } else {
      styling = { ...styling, ...advancedDifficultyStyling };
    }

    return styling;
  };

  return (
    <Button aria-pressed={selected} onClick={onClick} sx={buttonStyling}>
      <Box
        sx={{
          display: "grid",
          gap: 0.625,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
          }}
        >
          {activity.title}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.8rem",
          }}
        >
          <ActivityText>{description ?? ""}</ActivityText>
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          color: theme.palette.text.secondary,
          alignSelf: "start",
        }}
      >
        {activity.content.estimatedTime && (
          <Typography
            sx={{
              fontSize: "0.75rem",
            }}
          >
            ~{activity.content.estimatedTime}
          </Typography>
        )}
        {levelDetailsLabel && (
          <Typography sx={difficultyStyling}>{levelDetailsLabel}</Typography>
        )}
      </Box>
    </Button>
  );
};
